import { EmployeeRow } from 'components/Employee/EmployeeRow';
import { LinearProgress } from 'components/LinearProgress';
import { FC, useEffect } from 'react';
import { IEmployeeList } from 'types/Employee';

import LeftIcon from '@mui/icons-material/ChevronLeft';
import RightIcon from '@mui/icons-material/ChevronRight';
import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Fab,
  Grid,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  Zoom,
} from '@mui/material';

export interface IEmployeeListProps extends IEmployeeList {
  currentPage: number;
  setPage: Function;
  maxPage: number;
  loading: boolean;
}

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.primary.main,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.primary.main,
    maxWidth: 220,
  },
}));

export const EmployeeList: FC<IEmployeeListProps> = ({
  list,
  currentPage,
  setPage,
  maxPage,
  loading,
}) => {
  const previousPage = () => {
    if (currentPage > 1) {
      setPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    setPage(currentPage + 1);
  };

  useEffect(() => {
    setPage(1);
  }, [maxPage, setPage]);

  return (
    <TableContainer component={Paper} sx={{ mt: 5, mb: 2 }}>
      {loading && <LinearProgress />}
      <Table size="small" aria-label="a dense table">
        <TableHead>
          <TableRow>
            <TableCell />

            <TableCell>
              <strong>Nome Completo</strong>
            </TableCell>
            <TableCell>
              <strong>E-mail</strong>
            </TableCell>
            <TableCell>
              <Grid container spacing={1}>
                <Grid item alignSelf="center">
                  <strong>Vínculo Empregatício</strong>
                </Grid>
                <Grid item>
                  <StyledTooltip
                    arrow
                    placement="right"
                    title="Todos os colaboradores que fazem parte da empresa."
                    TransitionComponent={Zoom}
                    color="#FBB45F"
                  >
                    <InfoIcon
                      cursor="pointer"
                      sx={{ color: '#FBB45F', verticalAlign: 'top' }}
                    />
                  </StyledTooltip>
                </Grid>
              </Grid>
            </TableCell>
            <TableCell>
              <Grid container spacing={1}>
                <Grid item alignSelf="center">
                  <strong>Status do Benefício</strong>
                </Grid>
                <Grid item>
                  <StyledTooltip
                    arrow
                    placement="right"
                    title="Os colaboradores ativos são os que finalizaram o cadastro no aplicativo e usufruem do benefício."
                    TransitionComponent={Zoom}
                    color="#FBB45F"
                  >
                    <InfoIcon
                      cursor="pointer"
                      sx={{ color: '#FBB45F', verticalAlign: 'top' }}
                    />
                  </StyledTooltip>
                </Grid>
              </Grid>
            </TableCell>
            <TableCell align="center" width={50}>
              <strong>Ação</strong>
            </TableCell>
          </TableRow>
        </TableHead>

        {list?.length < 1 && (
          <Typography component="caption">
            <Box textAlign="center">
              Não existem colaboradores cadastrados ainda.
            </Box>
          </Typography>
        )}

        <TableBody>
          {list?.map((employee) => (
            <EmployeeRow key={employee.id} employee={employee} />
          ))}
        </TableBody>
      </Table>

      <Box textAlign="right" m={2}>
        <Typography>
          <Fab
            color="primary"
            size="small"
            aria-label="remove"
            disabled={currentPage <= 1}
            onClick={previousPage}
          >
            <LeftIcon />
          </Fab>
          <Fab disabled size="small" aria-label="add" sx={{ ml: 1, mr: 1 }}>
            {currentPage}
          </Fab>
          <Fab
            color="primary"
            size="small"
            aria-label="add"
            disabled={currentPage === maxPage || list?.length < 1}
            onClick={nextPage}
          >
            <RightIcon />
          </Fab>
        </Typography>
      </Box>
    </TableContainer>
  );
};
